export interface MessageQuote {
  id: string;
  author: string;
  text: string;
}

export interface MessageReaction {
  pseudo: string;
  emoji: string;
}

export interface ReactionGroup {
  emoji: string;
  count: number;
  users: string[];
  includesMe: boolean;
}

export type GameInviteRecipientState =
  | 'joined'
  | 'invited'
  | 'accepted'
  | 'declined'
  | 'unavailable'
  | 'offline'
  | 'cancelled';

export type GameInviteStatus = 'pending' | 'cancelled' | 'expired' | 'completed';

export interface GameInviteRecipient {
  pseudo: string;
  pseudoDisplay: string;
  state: GameInviteRecipientState;
}

export interface GameInvitePayload {
  draftId: string;
  status: GameInviteStatus;
  compet: boolean;
  open?: boolean;
  config: {
    gameMode: string;
    nombreJoueurs: number;
  };
  recipients: GameInviteRecipient[];
  createdAt: string;
  updatedAt: string;
}

export interface LibrarySharePayload {
  sharedGameId: number;
  originType: string;
  contract: number | null;
  isPoll: boolean;
  shareComment?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CiaAgendaInviteEtat =
  | 'attente'
  | 'ok'
  | 'refus'
  | 'annule_auteur'
  | 'annule_destinataire'
  | 'annule_bot';

export interface CiaAgendaInvitePayload {
  agendaId: number;
  phaseId: number;
  etat: CiaAgendaInviteEtat;
  rdv: string;
  proposerPseudo: string;
  proposerDisplay: string;
  recipientPseudo: string;
  recipientDisplay: string;
  createdAt: string;
  updatedAt: string;
}

export const LIBRARY_SHARE_CONTRACT_LABELS: Record<number, string> = {
  0: 'Passe',
  1: 'Petite',
  2: 'Garde',
  3: 'G. Sans',
  4: 'G. Contre',
};

export const LIBRARY_SHARE_ORIGIN_LABELS: Record<string, string> = {
  libre: 'Libre',
  differe_indiv: 'Diff. indiv.',
  differe_tq: 'Diff. TQ',
  defi: 'Défi',
  training: 'Entraînement',
};

export const GAME_INVITE_RECIPIENT_LABELS: Record<GameInviteRecipientState, string> = {
  joined: 'A rejoint la table',
  invited: 'En attente de réponse',
  accepted: 'A accepté',
  declined: 'A refusé',
  unavailable: 'Indisponible',
  offline: 'Hors ligne — en attente',
  cancelled: 'Invitation annulée',
};

export interface ReplyTarget {
  id: string;
  author: string;
  text: string;
}

export function truncateQuoteText(text: string, maxLength = 120): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

export function toReplyTarget(quote: MessageQuote): ReplyTarget {
  return {
    id: quote.id,
    author: quote.author,
    text: quote.text,
  };
}

export function groupReactions(
  reactions: MessageReaction[],
  currentPseudo: string,
): ReactionGroup[] {
  const map = new Map<string, string[]>();
  for (const reaction of reactions) {
    const users = map.get(reaction.emoji) ?? [];
    users.push(reaction.pseudo);
    map.set(reaction.emoji, users);
  }

  const current = currentPseudo.toLowerCase();
  return [...map.entries()].map(([emoji, users]) => ({
    emoji,
    count: users.length,
    users,
    includesMe: users.some(u => u.toLowerCase() === current),
  }));
}
